import { type FC, useEffect, useState } from "react";

import { useIsAssessing } from "../section/assessment/hooks";

import { AutoFollowButtons } from "./AutoFollowButtons";
import { FollowButton } from "./FollowButton";
import { MovePresentationButton } from "./MovePresentationButton";
import { ProjectingButton } from "./ProjectingButton";
import { StopPresentingButton } from "./StopPresentingButton";
import { openProjectorView } from "./projector/projector-messaging";

import { withActiveAv } from "components/av/AvSubsystemLoader";
import { PresentationBanner } from "components/footer/PresentationBanner";
import { PagePresentingMenu } from "components/materials/presentation/PagePresentingMenu";
import { ReturnToDefaultTabButton } from "components/materials/presentation/ReturnToDefaultTabButton";
import { StopFollowingButton } from "components/materials/presentation/StopFollowingButton";
import { TakeOverPresentationButton } from "components/materials/presentation/TakeOverPresentationButton";
import { useIsLgOrLarger } from "mds/hooks/use-responsive";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectCurrentPage,
  selectCurrentUserId,
  selectIsDesktopDevice,
  selectIsPracticeClassSession,
} from "store/selectors";
import { selectCurrentClassUserIsPerformingAvCheck } from "store/selectors/class-user";
import { selectIsObservingVirtualClass } from "store/selectors/pubsub-virtual-class-state";
import { useJoinAsSectionAdmin } from "utils/hooks/use-join-as-section-admin";
import {
  presentSession,
  setPresentationWindow,
  startProjecting,
  stopPresenting,
  usePresentation,
  usePresentationBannerColoring,
  usePresentationBannerText,
} from "utils/presentation";
import { useViewLevel } from "utils/urls";

const LazyScreenshareButton = withActiveAv(() => import("components/av/ScreenshareButton"));

export type PresentOptions = {
  isRollup?: boolean;
  hasProjectorView?: boolean;
  isPractice?: boolean;
  accessId?: string | null;
};

export type PresentationOptionsProps = {
  isInClassSession?: boolean;
};

export const PresentationOptions: FC<PresentationOptionsProps> = ({ isInClassSession = false }) => {
  const selectedPage = useAppSelector(selectCurrentPage);
  const isLargeScreen = useIsLgOrLarger();
  const {
    presentedSessionId,
    presentedPageId,
    isFollowing,
    isPresenting,
    isPresentingUser,
    unfeaturedTab,
    sectionId,
    isFollowingDisconnected,
  } = usePresentation();
  const { joinAsSectionAdminIfNew } = useJoinAsSectionAdmin();
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);
  const isPracticeClassSession = useAppSelector(selectIsPracticeClassSession);
  const isPerformingAvCheck = useAppSelector(selectCurrentClassUserIsPerformingAvCheck);
  const isDesktopDevice = useAppSelector(selectIsDesktopDevice);
  const viewLevel = useViewLevel();

  const isAssessing = useIsAssessing();
  const { canAuthorSection, canParticipateInSection, canPresentSection } = useCapabilities();
  const userId = useAppSelector(selectCurrentUserId);

  const isPageView = viewLevel.page;
  const { bannerText, tooltipText } = usePresentationBannerText(viewLevel, !isLargeScreen);
  const color = usePresentationBannerColoring();

  // Track if user has ever followed this presentation session - if so, don't auto-follow again
  const [hasFollowedThisSession, setHasFollowedThisSession] = useState(false);
  useEffect(() => {
    if (isFollowing) {
      setHasFollowedThisSession(true);
    }
  }, [isFollowing]);

  // Reset when presentation ends
  useEffect(() => {
    if (!presentedPageId) {
      setHasFollowedThisSession(false);
    }
  }, [presentedPageId]);

  const onPresent = async (options: PresentOptions = {}) => {
    if (!presentedSessionId || !selectedPage) return;
    await joinAsSectionAdminIfNew();

    setPresentationWindow();
    return presentSession(presentedSessionId, { page: selectedPage, ...options, userId });
  };

  const onStopPresenting = () => {
    if (!presentedSessionId) return;
    return stopPresenting(presentedSessionId);
  };

  const onProjecting = async () => {
    if (!presentedSessionId || !selectedPage || !sectionId) return;
    openProjectorView(sectionId);
    return startProjecting(presentedSessionId, {
      // We add these fields to ensure the server validation passes,
      // just in case the projecting action is taken before any presenting action.
      presented_page_id: selectedPage.id,
      presenting_user_id: userId,
    });
  };

  if (!presentedPageId) {
    if (canPresentSection && isPageView) {
      return (
        <PagePresentingMenu
          isDisabled={selectedPage?.category === "instructor_workspace" || isAssessing}
          isPractice={isPracticeClassSession}
          size="m"
          onPractice={() => onPresent({ isPractice: true })}
          onPresent={onPresent}
        />
      );
    }

    return null;
  }
  if (isPresenting) {
    return (
      <>
        <PresentationBanner bannerText={bannerText} color={color} tooltipText={tooltipText}>
          {isDesktopDevice && <LazyScreenshareButton color={color} />}
          <ReturnToDefaultTabButton color={color} />
          {canPresentSection && (
            <StopPresentingButton
              color={color}
              eventName={
                isPracticeClassSession ? "Presentation - End practice" : "Presentation - End"
              }
              showShortText={!isLargeScreen || isPracticeClassSession}
              onStopPresenting={onStopPresenting}
            />
          )}
        </PresentationBanner>

        {!unfeaturedTab && isLargeScreen && <ProjectingButton onProject={onProjecting} />}
      </>
    );
  }

  const isFollowingAsAStudent = isFollowing && canParticipateInSection;
  const isNotFollowingThePresenter = !isFollowing && !isPresentingUser;

  // Only show auto-follow if user hasn't already followed during this presentation session
  // and is not currently in AV check
  const shouldAutoFollow = isInClassSession && !hasFollowedThisSession && !isPerformingAvCheck;

  // Not presenting at all, or at least not presenting in this tab given the presentations
  // are tied to a specific tab.
  return (
    <PresentationBanner bannerText={bannerText} color={color} tooltipText={tooltipText}>
      {canAuthorSection && isDesktopDevice && <LazyScreenshareButton color={color} />}

      {canPresentSection && !isObservingClass && <TakeOverPresentationButton color={color} />}

      {isNotFollowingThePresenter && <AutoFollowButtons allowAutoFollow={shouldAutoFollow} />}

      {isFollowingAsAStudent && isFollowingDisconnected && (
        <FollowButton color={color} showReturnText />
      )}

      {isFollowingAsAStudent && !isFollowingDisconnected && <StopFollowingButton color={color} />}

      {isPresentingUser && isPageView && (
        <MovePresentationButton
          color={color}
          onClick={() =>
            onPresent({ accessId: null, isRollup: false, isPractice: isPracticeClassSession })
          }
        />
      )}

      {isPresentingUser && (
        <StopPresentingButton
          color={color}
          eventName={isPracticeClassSession ? "Presentation - End practice" : "Presentation - End"}
          onStopPresenting={onStopPresenting}
        />
      )}
    </PresentationBanner>
  );
};
